import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, PencilIcon, TrashIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { environment } from './environment';

function getToken() {
  return localStorage.getItem("token");
}

const ActivityDetail = () => {
  const { activityId } = useParams();
  const navigate = useNavigate();
  
  const [activity, setActivity] = useState(null);
  const [subActivities, setSubActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSubActivityName, setNewSubActivityName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSubActivity, setSelectedSubActivity] = useState(null);
  const [editSubActivityName, setEditSubActivityName] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState('');
  
  useEffect(() => {
    fetchActivity();
    fetchSubActivities();
  }, [activityId]);

  const fetchActivity = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${environment.apiUrl}/activities/${activityId}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setActivity(data);
      } else {
        setError('Failed to fetch activity');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubActivities = async () => {
    try {
      const response = await fetch(`${environment.apiUrl}/sub-activities/`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        // Filter sub-activities that belong to this activity
        const filteredSubActivities = data.filter(sub => sub.activity_id === activityId);
        setSubActivities(filteredSubActivities);
      }
    } catch (err) {
      console.error('Error fetching sub-activities:', err);
    }
  };

  const handleAddSubActivity = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');

    try {
      const steps = newSubActivityName.split('\n').filter(step => step.trim() !== '');
      
      for (const stepName of steps) {
        const response = await fetch(`${environment.apiUrl}/sub-activities/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify({
            activity_id: activityId,
            name: stepName.trim()
          }),
        });

        if (!response.ok) {
          const data = await response.json();
          setSubmitError(data.detail || 'Failed to create sub-activity');
          return;
        }
      }

      setShowAddModal(false);
      setNewSubActivityName('');
      fetchSubActivities();
    } catch (err) {
      setSubmitError('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSubActivity = (subActivity) => {
    setSelectedSubActivity(subActivity);
    setEditSubActivityName(subActivity.name);
    setEditError('');
    setShowEditModal(true);
  };

  const handleUpdateSubActivity = async (e) => {
    e.preventDefault();
    setEditSubmitting(true);
    setEditError('');

    try {
      const response = await fetch(`${environment.apiUrl}/sub-activities/${selectedSubActivity.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          activity_id: activityId,
          name: editSubActivityName.trim()
        }),
      });

      if (response.ok) {
        setShowEditModal(false);
        setSelectedSubActivity(null);
        setEditSubActivityName('');
        fetchSubActivities();
      } else {
        const data = await response.json();
        setEditError(data.detail || 'Failed to update sub-activity');
      }
    } catch (err) {
      setEditError('Network error');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDeleteSubActivity = (subActivity) => {
    setSelectedSubActivity(subActivity);
    setShowDeleteModal(true);
  };

  const confirmDeleteSubActivity = async () => {
    if (!selectedSubActivity) return;
    
    try {
      const response = await fetch(`${environment.apiUrl}/sub-activities/${selectedSubActivity.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.ok) {
        setShowDeleteModal(false);
        setSelectedSubActivity(null);
        fetchSubActivities();
      } else {
        setError('Failed to delete sub-activity');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setShowDeleteModal(false);
      setSelectedSubActivity(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !activity) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          {error || 'Activity not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-pink-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/settings?tab=activity')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5 text-slate-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{activity.name}</h1>
                <p className="text-sm text-slate-500">Activity Details</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/settings?tab=activity')}
              className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              Back to Settings
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Activity Info */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-200 sticky top-24">
              <h2 className="text-lg font-semibold text-slate-900 mb-6">Activity Information</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="text-sm font-medium text-slate-500 block mb-2">Name</label>
                  <p className="text-base text-slate-900 font-semibold">{activity.name}</p>
                </div>

                {activity.short_term && (
                  <div>
                    <label className="text-sm font-medium text-slate-500 block mb-2">Short Term</label>
                    <p className="text-base text-slate-700">{activity.short_term}</p>
                  </div>
                )}

                {activity.activity_setting && (
                  <div>
                    <label className="text-sm font-medium text-slate-500 block mb-2">Setting</label>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-pink-100 text-pink-800">
                      {activity.activity_setting}
                    </span>
                  </div>
                )}

                {activity.description && (
                  <div>
                    <label className="text-sm font-medium text-slate-500 block mb-2">Description</label>
                    <p className="text-base text-slate-700 whitespace-pre-wrap leading-relaxed">
                      {activity.description}
                    </p>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-slate-500 block mb-2">Created</label>
                  <p className="text-sm text-slate-600">
                    {new Date(activity.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Sub-Activities */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-200">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-slate-900">
                  Sub-Activities ({subActivities.length})
                </h2>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors text-sm font-medium"
                >
                  <PlusIcon className="w-4 h-4" />
                  Add Sub-Activity
                </button>
              </div>

              {subActivities.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                  </div>
                  <p className="text-slate-600 font-medium mb-2">No sub-activities yet</p>
                  <p className="text-sm text-slate-500">Add sub-activities to define the process for this activity</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {subActivities.map((subActivity, index) => (
                    <div
                      key={subActivity.id}
                      className="flex items-start gap-4 p-4 bg-gradient-to-r from-slate-50 to-white border border-slate-200 rounded-lg hover:shadow-md transition-all duration-200"
                    >
                      <div className="flex-shrink-0 w-8 h-8 bg-pink-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-base text-slate-900 font-medium break-words">
                          {subActivity.name}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          Created {new Date(subActivity.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditSubActivity(subActivity)}
                          className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200 hover:shadow-sm"
                          title="Edit sub-activity"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteSubActivity(subActivity)}
                          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200 hover:shadow-sm"
                          title="Delete sub-activity"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Sub-Activity Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-slate-900">Add Sub-Activity</h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewSubActivityName('');
                  setSubmitError('');
                }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-slate-600" />
              </button>
            </div>

            <form onSubmit={handleAddSubActivity}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Sub-Activity Name(s)
                </label>
                <textarea
                  value={newSubActivityName}
                  onChange={(e) => setNewSubActivityName(e.target.value)}
                  placeholder="Enter sub-activity name (one per line for multiple items)"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 resize-none"
                  rows={5}
                  required
                />
                <p className="text-xs text-slate-500 mt-2">
                  Tip: Enter multiple sub-activities, one per line, to create them all at once
                </p>
              </div>

              {submitError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {submitError}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setNewSubActivityName('');
                    setSubmitError('');
                  }}
                  className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors font-medium disabled:bg-pink-300 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Adding...' : 'Add Sub-Activity'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Sub-Activity Modal */}
      {showEditModal && selectedSubActivity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-slate-900">Edit Sub-Activity</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedSubActivity(null);
                  setEditSubActivityName('');
                  setEditError('');
                }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-slate-600" />
              </button>
            </div>

            <form onSubmit={handleUpdateSubActivity}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Sub-Activity Name
                </label>
                <input
                  type="text"
                  value={editSubActivityName}
                  onChange={(e) => setEditSubActivityName(e.target.value)}
                  placeholder="Enter sub-activity name"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                  required
                />
              </div>

              {editError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {editError}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedSubActivity(null);
                    setEditSubActivityName('');
                    setEditError('');
                  }}
                  className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editSubmitting}
                  className="flex-1 px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors font-medium disabled:bg-pink-300 disabled:cursor-not-allowed"
                >
                  {editSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Sub-Activity Modal */}
      {showDeleteModal && selectedSubActivity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 text-center">
            <h3 className="text-xl font-bold text-red-700 mb-4">Delete Sub-Activity</h3>
            <p className="mb-6 text-slate-700">
              Are you sure you want to delete <span className="font-semibold">{selectedSubActivity.name}</span>? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={confirmDeleteSubActivity}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Delete
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedSubActivity(null);
                }}
                className="px-6 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityDetail;
